import type { OrderTypeToSoundMapType } from '@src/providers/SoundProvider';
import type { RfqBestQuoteBy } from '@src/utils/rfq';
import type {
  ContextGuideID,
  DateTimeDurationPickerValueType,
  FeeModeEnum,
  PersistedTourState,
  PriceInputMode,
  RFQTradingProtocolEnum,
  SecurityType,
  Slippage,
  TabProps,
} from '@talos/kyoko';
import { DEFAULT_MONITORING_LAYOUT_SPEC } from '../../components/AppLayout/Layouts/Monitoring/DefaultMonitoringLayoutSpec';
import { DEFAULT_LAYOUT_ID } from '../../components/AppLayout/Layouts/tokens';
import { DEFAULT_TRADING_LAYOUT_SPEC } from '../../components/AppLayout/Layouts/Trading/DefaultTradingLayoutSpec';
import type { OMSPopoutGroup, OMSPopoutGroupType } from '../../components/OMS/types';
import type { PortfolioViewLayoutState } from '../../containers/Portfolio/PortfolioManagement/stateManagement/portfolioViewLayoutSlice';
import type { OrderStrategiesEnum } from '../../tokens/orderStrategy';
import type { LayoutSection, LayoutSpec, PanelConfig, SpacesSectionConfig } from '../../types/LayoutConfig';
import { VALID_SECTIONS } from '../../types/LayoutConfig';
import type { IOrderPreset } from '../OrderPresetsContext';
import type { NewMultilegDefaultDelta1Behaviour } from '../TradingSettingsContext.types';
import type { IDDHOrderDefault } from './AppConfigProvider';
import type { AppConfigState } from './AppConfigProvider.types';
import { convertMarketTabsToDockviewLayout } from './convertMarketTabsToDockviewLayout';

export const createMethods = (state: AppConfigState) => ({
  // User
  setTermsAccepted: (termsAccepted: boolean) => ({
    ...state,
    termsAccepted: termsAccepted ? new Date().toISOString() : null,
  }),

  // Display settings
  setShowAllInPrices: (showAllInPrices: FeeModeEnum | null) => ({ ...state, showAllInPrices }),
  setShowFirmLiquidity: (showFirmLiquidity: boolean) => ({ ...state, showFirmLiquidity }),
  setShowDelta1BPS: (showDelta1BPS: boolean) => ({ ...state, showDelta1BPS }),
  setShowTotalEstimates: (showTotalEstimates: boolean) => ({ ...state, showTotalEstimates }),
  setHomeCurrency: (homeCurrency: string) => ({ ...state, homeCurrency }),
  setPinNavigationSidebarSection: (pinNavigationSidebar: boolean, section: string) => ({
    ...state,
    pinNavigationSidebarSection: { ...state.pinNavigationSidebarSection, [section]: pinNavigationSidebar },
  }),
  setLatestReleaseNotesRead: (latestReleaseNotesRead: string | undefined) => ({ ...state, latestReleaseNotesRead }),
  setOTCSettlementShowAll: (otcSettlementShowAll: boolean) => ({ ...state, otcSettlementShowAll }),
  setShowSidebarLabels: (showSidebarLabels: boolean) => ({ ...state, showSidebarLabels }),
  setSeenTours: (seenTours: PersistedTourState) => ({ ...state, seenTours }),
  setSeenShowcases: (seenShowcases: AppConfigState['seenShowcases']) => ({ ...state, seenShowcases }),
  // Trading
  setAllowSyntheticCcy: (allowSyntheticCcy: boolean) => ({ ...state, allowSyntheticCcy }),
  setAllowedSlippage: (allowedSlippage: Slippage) => ({ ...state, allowedSlippage }),
  setDefaultSubAccountId: (defaultSubAccountId: number | undefined) => ({ ...state, defaultSubAccountId }),
  setEnableGroups: (enableGroups: boolean) => ({ ...state, enableGroups }),
  setConfirmOrderCancels: (confirmOrderCancels: boolean) => ({ ...state, confirmOrderCancels }),
  setConfirmOrderResume: (confirmOrderResume: boolean) => ({ ...state, confirmOrderResume }),
  setRememberSubAccountId: (rememberSubAccountId: boolean) => ({ ...state, rememberSubAccountId }),
  setUseTradeAllocations: (useTradeAllocations: boolean) => ({ ...state, useTradeAllocations }),
  setExportDatesRaw: (exportDatesRaw: boolean) => ({ ...state, exportDatesRaw }),
  setCustomEOD: (customEOD: { hours?: string | number; minutes?: string | number }) => ({ ...state, customEOD }),
  setClickToTradeAll: (clickToTradeAll: boolean) => ({ ...state, clickToTradeAll }),
  setClickToTradeDefaultSubaccount: (clickToTradeDefaultSubaccount: string) => ({
    ...state,
    clickToTradeDefaultSubaccount,
  }),
  setAllowSyntheticCrosses: (allowSyntheticCrosses: boolean) => ({ ...state, allowSyntheticCrosses }),
  setAlwaysCheckPriceReasonability: (alwaysCheckPriceReasonability: boolean) => ({
    ...state,
    alwaysCheckPriceReasonability,
  }),
  setEnableCustomerTradeBookingOnCPC: (enableCustomerTradeBookingOnCPC: boolean) => ({
    ...state,
    enableCustomerTradeBookingOnCPC,
  }),
  setDefaultOrderStrategy: (orderStrategy: string) => ({ ...state, defaultOrderStrategy: orderStrategy }),
  setDefaultMultilegOrderStrategy: (orderStrategy: string) => ({
    ...state,
    defaultMultilegOrderStrategy: orderStrategy,
  }),
  setDefaultOrderFormTab: (orderTab: string | undefined) => ({ ...state, defaultOrderFormTab: orderTab }),
  setEnableDerivativeContractDefault: (enableDerivativeContractDefault: boolean) => ({
    ...state,
    enableDerivativeContractDefault,
  }),
  setExclusivelyPrimePriceOnRePriming: (exclusivelyPrimePriceOnRePriming: boolean) => ({
    ...state,
    exclusivelyPrimePriceOnRePriming,
  }),
  setDefaultPriceOffsets: (priceOffsets: string) => ({ ...state, defaultPriceOffsets: priceOffsets }),
  setAlwaysUseBBOForPriceOffsets: (alwaysUseBBOForPriceOffsets: boolean) => ({
    ...state,
    alwaysUseBBOForPriceOffsets,
  }),
  setStartTimeEntryMode: (startTimeEntryMode: DateTimeDurationPickerValueType) => ({ ...state, startTimeEntryMode }),
  setEndTimeEntryMode: (endTimeEntryMode: DateTimeDurationPickerValueType) => ({ ...state, endTimeEntryMode }),
  setEnableAdvancedOrderDetails: (enableAdvancedOrderDetails: boolean) => ({ ...state, enableAdvancedOrderDetails }),
  setAlwaysShowBestBidOffer: (alwaysShowBestBidOffer: boolean) => ({ ...state, alwaysShowBestBidOffer }),
  setAlwaysShowMarketCardBestBidOffer: (alwaysShowMarketCardBestBidOffer: boolean) => ({
    ...state,
    alwaysShowMarketCardBestBidOffer,
  }),
  setShowOrderFormRollupSelector: (showOrderFormRollupSelector: boolean) => ({ ...state, showOrderFormRollupSelector }),
  setUseNewMarketSelector: (useNewMarketSelector: boolean) => ({ ...state, useNewMarketSelector }),
  setBulkClosePositionQuoteCurrencies: (bulkClosePositionQuoteCurrencies: string[]) => ({
    ...state,
    bulkClosePositionQuoteCurrencies,
  }),
  setEnableReduceFirst: (enableReduceFirst: boolean) => ({ ...state, enableReduceFirst }),
  setEnableReduceOnly: (enableReduceOnly: boolean) => ({ ...state, enableReduceOnly }),
  setPriceOffsetBPSBehaviour: (priceOffsetBPSBehaviour: PriceInputMode) => ({ ...state, priceOffsetBPSBehaviour }),
  setDefaultToEmptyOrderMarkets: (defaultToEmptyOrderMarkets: boolean) => ({ ...state, defaultToEmptyOrderMarkets }),
  setResetOrderAccountsOnSubAccountChange: (resetOrderAccountsOnSubAccountChange: boolean) => ({
    ...state,
    resetOrderAccountsOnSubAccountChange,
  }),

  // Trading -> Customer Pricing
  setEnableCustomerPricing: (enableCustomerPricing: boolean) => ({ ...state, enableCustomerPricing }),
  setEnableSalesWorkflow: (enableSalesWorkflow: boolean) => ({ ...state, enableSalesWorkflow }),
  setLinkCustomerHedgeSubaccount: (linkCustomerHedgeSubaccount: boolean) => ({ ...state, linkCustomerHedgeSubaccount }),
  setEnableCustomerBlotterColumns: (enableCustomerBlotterColumns: boolean) => ({
    ...state,
    enableCustomerBlotterColumns,
  }),
  setCustomerPricingInitialExpanded: (customerPricingInitialExpanded: boolean) => ({
    ...state,
    customerPricingInitialExpanded,
  }),
  setApplyCustomerPricingDetailsToOrderComment: (applyCustomerPricingDetailsToOrderComment: boolean) => ({
    ...state,
    applyCustomerPricingDetailsToOrderComment,
  }),
  setSelectedCustomerID: (selectedCustomerID: number | undefined) => ({ ...state, selectedCustomerID }),
  // Dealer
  setEnableMonitoringCustomerOrderSummaryColumns: (enableMonitoringCustomerOrderSummaryColumns: boolean) => ({
    ...state,
    enableMonitoringCustomerOrderSummaryColumns,
  }),
  setFilterValueCustomerUsers: (filterValueCustomerUsers: string) => ({ ...state, filterValueCustomerUsers }),
  setFilterValueCustomerMarketAccounts: (filterValueCustomerMarketAccounts: string) => ({
    ...state,
    filterValueCustomerMarketAccounts,
  }),
  setFilterValueAggregations: (filterValueAggregations: string) => ({ ...state, filterValueAggregations }),
  setFilterValueSecurityMaster: (filterValueSecurityMaster: string) => ({ ...state, filterValueSecurityMaster }),
  setFilterValueCustomerAddresses: (filterValueCustomerAddresses: string) => ({
    ...state,
    filterValueCustomerAddresses,
  }),
  setFilterValueCustomerBalances: (filterValueCustomerBalances: string) => ({ ...state, filterValueCustomerBalances }),
  setFilterValueCustomerTradingLimits: (filterValueCustomerTradingLimits: string) => ({
    ...state,
    filterValueCustomerTradingLimits,
  }),
  setFilterValueCustomerCredit: (filterValueCustomerCredit: string) => ({
    ...state,
    filterValueCustomerCredit,
  }),
  // Principal trading limits
  setFilterValueSubAccountTradingLimits: (filterValueSubAccountTradingLimits: string) => ({
    ...state,
    filterValueSubAccountTradingLimits,
  }),
  setFilterValueSubAccountWindowLimits: (filterValueSubAccountWindowLimits: string) => ({
    ...state,
    filterValueSubAccountWindowLimits,
  }),

  // Blotters
  setGroupMarketAccounts: (groupMarketAccounts: boolean) => ({ ...state, groupMarketAccounts }),
  setBalancesHiddenSymbols: (balancesHiddenSymbols: string[]) => ({ ...state, balancesHiddenSymbols }),
  setBalancesHiddenMarketAccountIDs: (balancesHiddenMarketAccountIDs: string[]) => ({
    ...state,
    balancesHiddenMarketAccountIDs,
  }),
  setBalancesMarketAccountIDsOrder: (balancesMarketAccountIDsOrder: string[]) => ({
    ...state,
    balancesMarketAccountIDsOrder,
  }),
  setBalancesCurrenciesOrder: (balancesCurrenciesOrder: string[]) => ({ ...state, balancesCurrenciesOrder }),
  setIsExpanded: (isExpanded: boolean) => ({ ...state, isExpanded }),
  setIsMinimized: (isMinimized: boolean) => ({ ...state, isMinimized }),

  setSessionBlotterHeight: (sessionBlotterHeight: number) => ({ ...state, sessionBlotterHeight }),
  setCustomerBalancesShowZeroBalances: (customerBalancesShowZeroBalances: boolean) => ({
    ...state,
    customerBalancesShowZeroBalances,
  }),

  setLastOrderFormUsed: (lastOrderFormUsed: string) => ({ ...state, lastOrderFormUsed }),

  // Symbol selector
  setRecentSymbols: (recentSymbols: Record<string, number[]>) => ({ ...state, recentSymbols }),

  // Order entry
  setLastStrategyUsed: (lastStrategyUsed: OrderStrategiesEnum) => ({ ...state, lastStrategyUsed }),
  setDDHDefaults: (ddhDefaults: IDDHOrderDefault[]) => ({ ...state, ddhDefaults }),

  // Sounds
  setEnableSoundEffects: (enableSoundEffects: boolean) => ({ ...state, enableSoundEffects }),
  setEnabledSounds: (enabledSounds: { [K in OrderTypeToSoundMapType]: boolean }) => ({
    ...state,
    enabledSounds,
  }),

  // Tabs
  setTabsItems: (tabsID: string, items: TabProps[]) => ({
    ...state,
    tabs: { ...state.tabs, [tabsID]: { ...state.tabs[tabsID], items } },
  }),
  setTabsSelectedIndex: (tabsID: string, selectedIndex: number) => ({
    ...state,
    tabs: { ...state.tabs, [tabsID]: { ...state.tabs[tabsID], selectedIndex } },
  }),

  // Watchlist
  setIsVisible: (isVisible: boolean) => ({
    ...state,
    isVisible,
  }),
  setShowOnlyFavorites: (showOnlyFavorites: boolean) => ({
    ...state,
    showOnlyFavorites,
  }),
  setSecurityTypes: (securityTypes: SecurityType) => ({
    ...state,
    securityTypes,
  }),
  setFilteredCurrencies: (filteredCurrencies: string[]) => ({
    ...state,
    filteredCurrencies,
  }),

  // Favorite securities
  setFavoriteSecurities: (favoriteSecurities: string[]) => ({
    ...state,
    favoriteSecurities,
  }),

  setFavoriteStrategies: (favoriteStrategies: string[]) => ({
    ...state,
    favoriteStrategies,
  }),

  setFavoriteAggregations: (favoriteAggregations: string[]) => ({
    ...state,
    favoriteAggregations,
  }),

  setDefaultAggregationId: (defaultAggregationId: string | undefined) => ({
    ...state,
    defaultAggregationId,
  }),

  setFavoriteSubAccounts: (favoriteSubAccounts: string[]) => ({
    ...state,
    favoriteSubAccounts,
  }),

  setDefaultMarketAccount: (market: string, marketAccount: string) => {
    const defaultMarketAccounts = { ...state.defaultMarketAccounts };
    defaultMarketAccounts[market] = marketAccount;
    return { ...state, defaultMarketAccounts };
  },

  // Order presets
  setOrderPresets: (orderPresets: IOrderPreset[]) => ({
    ...state,
    orderPresetsList: orderPresets,
  }),

  deleteOrderPreset: (id: string) => {
    const orderPresetsList = state.orderPresetsList.filter(preset => preset.id !== id);
    return { ...state, orderPresetsList };
  },

  // Portfolio Management settings
  setEnablePMSLayout: (enablePMSLayout: boolean) => ({ ...state, enablePMSLayout: enablePMSLayout }),
  setTreatStablecoinsAsCash: (treatStablecoinsAsCash: boolean) => ({
    ...state,
    treatStablecoinsAsCash,
  }),
  setPortfolioViewState: (portfolioViewState: PortfolioViewLayoutState['viewState']) => ({
    ...state,
    portfolioViewState,
  }),

  // Context Guide
  setGuide: (guideID: ContextGuideID, value: boolean) => {
    return {
      ...state,
      contextGuide: { ...state.contextGuide, [guideID]: value },
    };
  },

  // Layout config
  setLayoutConfig: (section: LayoutSection, layoutConfig: LayoutSpec) => {
    const sec = section as LayoutSection;
    const prev = state.spacesConfigs[sec] ?? { initialized: false };
    const sectionLayouts = prev.layouts ?? [];
    const existingIndex = sectionLayouts.findIndex(l => l.id === layoutConfig.id);
    const updatedLayouts =
      existingIndex >= 0
        ? sectionLayouts.map((l, i) => (i === existingIndex ? layoutConfig : l))
        : [...sectionLayouts, layoutConfig];
    return {
      ...state,
      spacesConfigs: {
        ...state.spacesConfigs,
        [sec]: { initialized: prev.initialized, layouts: updatedLayouts },
      },
    };
  },

  setSpacesConfigs: (section: LayoutSection, sectionLayoutConfigs: LayoutSpec[]) => {
    const sec = section as LayoutSection;
    const prev = state.spacesConfigs[sec] ?? { initialized: false };
    return {
      ...state,
      spacesConfigs: {
        ...state.spacesConfigs,
        [sec]: { initialized: prev.initialized, layouts: sectionLayoutConfigs },
      },
    };
  },

  setSelectedLayoutId: (section: LayoutSection, selectedLayoutId: string) => ({
    ...state,
    selectedLayoutIds: { ...state.selectedLayoutIds, [section]: selectedLayoutId },
  }),

  updateLayoutPanelParams: (
    section: LayoutSection,
    layoutId: string,
    panelId: string,
    paramsPatch: Partial<PanelConfig>
  ) => {
    if (Object.keys(paramsPatch).length === 0) {
      return state;
    }

    const prevSpacesConfig = state.spacesConfigs[section] ?? { initialized: false };
    const layouts = prevSpacesConfig.layouts ?? [];

    const layoutIndex = layouts.findIndex(layout => layout.id === layoutId);
    if (layoutIndex < 0) {
      return state;
    }

    const targetLayout = layouts[layoutIndex];
    const targetPanel = targetLayout.dockViewLayout.panels?.[panelId];
    if (targetPanel == null) {
      return state;
    }

    const nextLayouts = [...layouts];
    nextLayouts[layoutIndex] = {
      ...targetLayout,
      dockViewLayout: {
        ...targetLayout.dockViewLayout,
        panels: {
          ...targetLayout.dockViewLayout.panels,
          [panelId]: {
            ...targetPanel,
            params: { ...targetPanel.params, ...paramsPatch },
          },
        },
      },
    };

    return {
      ...state,
      spacesConfigs: {
        ...state.spacesConfigs,
        [section]: { ...prevSpacesConfig, layouts: nextLayouts },
      },
    };
  },

  mergeSpacesSectionConfig: (section: LayoutSection, spacesConfig: Partial<SpacesSectionConfig>) => {
    const prev = state.spacesConfigs[section as LayoutSection] ?? { initialized: false };
    const { nav: _stripNav, ...prevWithoutNav } = prev as SpacesSectionConfig & { nav?: unknown };
    return {
      ...state,
      spacesConfigs: {
        ...state.spacesConfigs,
        [section]: { ...prevWithoutNav, ...spacesConfig },
      },
    };
  },

  resetLayoutConfig: () => {
    const resetConfig = structuredClone(DEFAULT_TRADING_LAYOUT_SPEC);
    // Set the grid size so its not an exact copy of the default layout
    resetConfig.dockViewLayout.grid.height = window.innerHeight;
    resetConfig.dockViewLayout.grid.width = window.innerWidth;
    const tradingPrev = state.spacesConfigs.trading ?? { initialized: false };
    const monitoringPrev = state.spacesConfigs.monitoring ?? { initialized: false };
    return {
      ...state,
      spacesConfigs: {
        ...state.spacesConfigs,
        trading: {
          initialized: tradingPrev.initialized,
          layouts: [resetConfig],
        },
        monitoring: {
          initialized: monitoringPrev.initialized,
          layouts: [DEFAULT_MONITORING_LAYOUT_SPEC],
        },
      },
      selectedLayoutIds: {
        ...state.selectedLayoutIds,
        trading: DEFAULT_LAYOUT_ID,
      },
    };
  },

  reImportTabs: () => {
    // MarketTabs are only available in the legacy config
    const marketTabs = (state as unknown as { marketTabs: any[] }).marketTabs;
    if (marketTabs) {
      const prevTrading = state.spacesConfigs.trading ?? { initialized: false };
      return {
        ...state,
        spacesConfigs: {
          ...state.spacesConfigs,
          trading: {
            initialized: prevTrading.initialized,
            layouts: [convertMarketTabsToDockviewLayout(marketTabs)],
          },
        },
        selectedLayoutIds: {
          ...state.selectedLayoutIds,
          trading: DEFAULT_LAYOUT_ID,
        },
      };
    }
  },

  // Space config
  updateSpace: (id: string, config: Partial<LayoutSpec>) => {
    const result = getLayoutConfigById(state, id);
    if (!result) {
      return state;
    }
    const { section, layoutConfig } = result;
    if (Object.keys(config).length === 0) {
      return state;
    }

    const sec = section as LayoutSection;
    const prevSc = state.spacesConfigs[sec] ?? { initialized: false };
    const layouts = prevSc.layouts ?? [];
    const { nav: _stripNav, ...prevWithoutNav } = prevSc as SpacesSectionConfig & { nav?: unknown };

    const nextLayouts = layouts.map(l => {
      if (l.id !== id) {
        return l;
      }
      return { ...layoutConfig, ...config } as LayoutSpec;
    });

    return {
      ...state,
      spacesConfigs: {
        ...state.spacesConfigs,
        [sec]: { ...prevWithoutNav, layouts: nextLayouts },
      },
    };
  },

  removeSpace: (id: string) => {
    if (id === DEFAULT_LAYOUT_ID) {
      return state;
    }
    const result = getLayoutConfigById(state, id);
    if (!result) {
      return state;
    }
    const { section } = result;
    const sec = section as LayoutSection;
    const prev = state.spacesConfigs[sec] ?? { initialized: false };
    const prevLayouts = state.spacesConfigs[sec]?.layouts ?? [];
    return {
      ...state,
      spacesConfigs: {
        ...state.spacesConfigs,
        [sec]: {
          initialized: prev.initialized,
          layouts: prevLayouts.filter(l => l.id !== id),
        },
      },
    };
  },

  // new market selector
  setMarketSelectorAdvancedMode: (advancedMode: boolean) => ({
    ...state,
    marketSelectorConfig: { ...state.marketSelectorConfig, advancedMode },
  }),
  setMarketSelectorShowDisabledItems: (showDisabledItems: boolean) => ({
    ...state,
    marketSelectorConfig: { ...state.marketSelectorConfig, showDisabledItems },
  }),
  setMarketSelectorCredentialsOnly: (credentialsOnly: boolean) => ({
    ...state,
    marketSelectorConfig: { ...state.marketSelectorConfig, credentialsOnly },
  }),
  setSelectionListShowAll: (selectionListShowAll: boolean) => ({
    ...state,
    marketSelectorConfig: { ...state.marketSelectorConfig, selectionListShowAll },
  }),

  setOmsPopoutGroups: (omsPopoutGroups: Partial<Record<OMSPopoutGroupType, Omit<OMSPopoutGroup, '_window'>>>) => {
    return {
      ...state,
      omsPopoutGroups,
    };
  },

  setEnableDefaultMarketCardSubAccount: (enableDefaultMarketCardSubAccount: boolean) => ({
    ...state,
    enableDefaultMarketCardSubAccount,
  }),

  setDefaultNewMarketCardsCompact: (defaultNewMarketCardsCompact: boolean) => ({
    ...state,
    defaultNewMarketCardsCompact,
  }),

  setNewMultilegDefaultDelta1Behaviour: (newMultilegDefaultDelta1Behaviour: NewMultilegDefaultDelta1Behaviour) => ({
    ...state,
    newMultilegDefaultDelta1Behaviour,
  }),

  setKeepFormOpenAfterSubmit: (keepFormOpenAfterSubmit: boolean) => {
    return {
      ...state,
      keepFormOpenAfterSubmit,
    };
  },

  setShowAllDecimalsInBlotters: (showAllDecimalsInBlotters: boolean) => {
    return {
      ...state,
      showAllDecimalsInBlotters,
    };
  },

  setRfqPricingPanelShowAllDecimals: (rfqPricingPanelShowAllDecimals: boolean) => {
    return {
      ...state,
      rfqPricingPanelShowAllDecimals,
    };
  },

  setTakerQuoteBreakdownShowAllDecimals: (takerQuoteBreakdownShowAllDecimals: boolean) => {
    return {
      ...state,
      takerQuoteBreakdownShowAllDecimals,
    };
  },

  setCareOrderNettingPreviewShowAllDecimals: (careOrderNettingPreviewShowAllDecimals: boolean) => {
    return {
      ...state,
      careOrderNettingPreviewShowAllDecimals,
    };
  },

  setRfqFormTradingProtocol: (rfqFormTradingProtocol: RFQTradingProtocolEnum | undefined) => {
    return {
      ...state,
      rfqFormTradingProtocol,
    };
  },

  setRfqBestQuoteBy: (rfqBestQuoteBy: RfqBestQuoteBy) => {
    return {
      ...state,
      rfqBestQuoteBy,
    };
  },
});

function getLayoutConfigById(state: AppConfigState, id: string) {
  for (const section of VALID_SECTIONS) {
    const layouts = state.spacesConfigs[section]?.layouts;
    const layoutConfig = layouts?.find(l => l.id === id);
    if (layoutConfig) {
      return { section, layoutConfig };
    }
  }
  return null;
}
